import {
  Button,
  BUTTON_SIZE,
  BUTTON_VARIANTS,
  PlusIcon,
} from "@abios/smorgasbord-components"
import { Trashcan } from "icons"
import {
  StyledRow,
  StyledSubtitle,
  StyledTitle,
} from "molecules/Modal/Modal.styles"
import {
  DomainModalContainer,
  CreateDomainInput,
  CreateDomainRow,
  DomainDeleteButton,
  ModalActionButton,
  ModalErrorText,
  DomainModalContentContainer,
} from "pages/widgets/components/modal/WidgetDomainModal.styles"
import { useWidgetDomains } from "pages/widgets/hooks/useWidgetDomains"
import { useCallback, useState } from "react"

type WidgetDomainModalProps = {
  namespace: string
  onClose: () => void
}

export const WidgetDomainModal = ({
  namespace,
  onClose,
}: WidgetDomainModalProps) => {
  const { domains, actions, isLoading } = useWidgetDomains(namespace)

  const [createDomainText, setCreateDomainText] = useState<string>("")
  const [hasCreateDomainError, setDomainError] = useState<boolean>(false)
  const [hasUpdateDomainError, setUpdateDomainError] = useState<boolean>(false)

  const createDomainErrorHint = hasCreateDomainError
    ? "this domain already exists for this brand"
    : ""

  const onHandleCreateDomain = useCallback(
    (domain: string) => {
      try {
        setDomainError(false)
        actions.onCreateDomain(domain)
        setCreateDomainText("")
      } catch {
        setDomainError(true)
      }
    },
    [actions],
  )

  const onHandleUpdateDomains = useCallback(async () => {
    try {
      setUpdateDomainError(false)
      actions.onUpdateDomains()
    } catch {
      setUpdateDomainError(true)
    }
  }, [actions])

  return (
    <DomainModalContainer>
      <StyledRow data-direction="column">
        <StyledTitle>{namespace}</StyledTitle>
        <StyledSubtitle>Domains</StyledSubtitle>
      </StyledRow>

      <DomainModalContentContainer>
        {domains?.domains.map((domain) => (
          <StyledRow
            key={domain.url}
            data-direction="row"
            data-align="between"
            data-items="center">
            <p>{domain.url}</p>
            <DomainDeleteButton
              type="button"
              onClick={() => actions.onRemoveDomain(domain.url)}>
              <Trashcan size={16} color="var(--red-400)" />
            </DomainDeleteButton>
          </StyledRow>
        ))}
      </DomainModalContentContainer>

      <CreateDomainRow>
        <CreateDomainInput
          value={createDomainText}
          onChange={(e) => setCreateDomainText(e.target.value)}
          placeholder="Enter a domain url"
          warned={hasCreateDomainError}
          hint={createDomainErrorHint}
        />
        <Button
          onClick={() => onHandleCreateDomain(createDomainText)}
          variant={BUTTON_VARIANTS.DEFAULT}
          size={BUTTON_SIZE.XXS}>
          <PlusIcon size="16" color="var(--gray-500)" />
        </Button>
      </CreateDomainRow>

      {hasUpdateDomainError && (
        <StyledRow>
          <ModalErrorText>
            There was an error trying to update the domains
          </ModalErrorText>
        </StyledRow>
      )}

      <StyledRow data-gap="1">
        <ModalActionButton
          size={BUTTON_SIZE.S}
          variant={BUTTON_VARIANTS.DEFAULT}
          onClick={onClose}
          disabled={isLoading}>
          Cancel
        </ModalActionButton>
        <ModalActionButton
          size={BUTTON_SIZE.S}
          onClick={onHandleUpdateDomains}
          isLoading={isLoading}>
          Update
        </ModalActionButton>
      </StyledRow>
    </DomainModalContainer>
  )
}
